import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { configurationApi, toRegisteredDeliverySettingsUpsertInput, type Certificate, type MediaRealm, type PeerGroup, type RegisteredDeliverySettings, type RoutingRule } from '../services/configurationApi'
import type { ConfigurationCodec } from '@sbclite/core'
import toast from 'react-hot-toast'

// Query Keys
export const queryKeys = {
  certificates: ['certificates'] as const,
  codecGroups: ['codecGroups'] as const,
  mediaRealms: ['mediaRealms'] as const,
  peerGroups: ['peerGroups'] as const,
  sipConfigurations: ['sipConfigurations'] as const,
  routingRules: ['routingRules'] as const,
  dashboard: ['dashboard'] as const,
  registeredDeliverySettings: ['registeredDeliverySettings'] as const,
}

export function useRegisteredDeliverySettings(enabled = true) {
  return useQuery<RegisteredDeliverySettings | null>({
    queryKey: queryKeys.registeredDeliverySettings,
    queryFn: configurationApi.getRegisteredDeliverySettings,
    enabled,
  })
}

export function useUpdateRegisteredDeliveryDeploymentId() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (pushGatewayDeploymentId: string) => {
      // Fetch immediately before writing so another administrator's changes are preserved.
      const current = await configurationApi.getRegisteredDeliverySettings()
      if (!current) {
        throw new Error('Registered-delivery settings are not available')
      }
      return configurationApi.updateRegisteredDeliverySettings(
        toRegisteredDeliverySettingsUpsertInput(current, pushGatewayDeploymentId),
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.registeredDeliverySettings })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

// Certificates Hooks
export function useCertificates() {
  return useQuery({
    queryKey: queryKeys.certificates,
    queryFn: configurationApi.getCertificates,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCreateCertificate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.createCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Certificate created successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create certificate')
    },
  })
}

export function useUpdateCertificate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Certificate> }) =>
      configurationApi.updateCertificate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Certificate updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update certificate')
    },
  })
}

export function useDeleteCertificate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.deleteCertificate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.certificates })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Certificate deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete certificate')
    },
  })
}

// Codec Groups Hooks
export function useCodecGroups() {
  return useQuery({
    queryKey: queryKeys.codecGroups,
    queryFn: configurationApi.getCodecGroups,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateCodecGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.createCodecGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.codecGroups })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Codec group created successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create codec group')
    },
  })
}

export function useUpdateCodecGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ConfigurationCodec> }) =>
      configurationApi.updateCodecGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.codecGroups })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Codec group updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update codec group')
    },
  })
}

export function useDeleteCodecGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.deleteCodecGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.codecGroups })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Codec group deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete codec group')
    },
  })
}

// Media Realms Hooks
export function useMediaRealms() {
  return useQuery({
    queryKey: queryKeys.mediaRealms,
    queryFn: configurationApi.getMediaRealms,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateMediaRealm() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.createMediaRealm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mediaRealms })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Media realm created successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create media realm')
    },
  })
}

export function useUpdateMediaRealm() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ realmId, data }: { realmId: string; data: Partial<MediaRealm> }) =>
      configurationApi.updateMediaRealm(realmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mediaRealms })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Media realm updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update media realm')
    },
  })
}

export function useDeleteMediaRealm() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.deleteMediaRealm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mediaRealms })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Media realm deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete media realm')
    },
  })
}

// Peer Groups Hooks
export function usePeerGroups() {
  return useQuery({
    queryKey: queryKeys.peerGroups,
    queryFn: configurationApi.getPeerGroups,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreatePeerGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.createPeerGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.peerGroups })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Peer group created successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create peer group')
    },
  })
}

export function useUpdatePeerGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PeerGroup> }) =>
      configurationApi.updatePeerGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.peerGroups })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Peer group updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update peer group')
    },
  })
}

export function useDeletePeerGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.deletePeerGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.peerGroups })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Peer group deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete peer group')
    },
  })
}

// Routing Rules Hooks
export function useRoutingRules() {
  return useQuery({
    queryKey: queryKeys.routingRules,
    queryFn: configurationApi.getRoutingRules,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateRoutingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: configurationApi.createRoutingRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Routing rule created successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create routing rule')
    },
  })
}

export function useUpdateRoutingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ routeId, ruleId, data }: { routeId: string; ruleId: string; data: Partial<RoutingRule> }) =>
      configurationApi.updateRoutingRule(routeId, ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Routing rule updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update routing rule')
    },
  })
}

export function useDeleteRoutingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ routeId, ruleId }: { routeId: string; ruleId: string }) =>
      configurationApi.deleteRoutingRule(routeId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
      toast.success('Routing rule deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete routing rule')
    },
  })
}

// Dashboard Hook
export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: configurationApi.getDashboardData,
    staleTime: 30 * 1000, // 30 seconds for dashboard data
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}

// System Health Hook
export function useSystemHealth(timeRange: '1h' | '6h' | '24h' | '7d' = '1h') {
  return useQuery({
    queryKey: ['systemHealth', timeRange],
    queryFn: () => configurationApi.getSystemHealth(timeRange, true),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}

// Performance Metrics Hook
export function usePerformanceMetrics() {
  return useQuery({
    queryKey: ['performanceMetrics'],
    queryFn: configurationApi.getPerformanceMetrics,
    staleTime: 5 * 60 * 1000, // 5 minutes for performance data
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  })
}

// SIP Registration Health Hook
export function useSipRegistrationHealth() {
  return useQuery({
    queryKey: ['sipRegistrationHealth'],
    queryFn: configurationApi.getSipRegistrationHealth,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}
